import {
  GlobalStateService,
  MarketsByProtocol,
  MarketsData,
  PERCENT_ZERO,
  ProtocolKey,
} from "@silo/types";
import { Cause, Console, Effect, pipe } from "effect";
import { getProtocolMarkets } from "./get-protocol-markets";
import { MarketsStrategy } from "./types";
import { MerklClient } from "../merkl-client";
import {
  createMerklCampaignMap,
  MerklCampaignMapService,
} from "../merkl-client/merkl-campaign-map";
import { sortMarketsByDefaultOrder } from "./utils";
import { MerklCampaign } from "../merkl-client/types";
import { getRevenueData } from "../revenue/get-revenue-data";

export type GetDisplayMarkets = ReturnType<typeof getDisplayMarkets>;
export type GetDisplayMarketsSuccess = Effect.Effect.Success<GetDisplayMarkets>;
export type GetDisplayMarketsError = Effect.Effect.Error<GetDisplayMarkets>;
export type GetDisplayMarketsContext = Effect.Effect.Context<GetDisplayMarkets>;

export function getDisplayMarkets(
  protocolKeys: ProtocolKey[],
  strategy: MarketsStrategy = "graph"
) {
  return Effect.gen(function* () {
    const [revenueApr, merklCampaigns] = yield* Effect.all(
      [
        pipe(
          getRevenueData(null),
          Effect.map((revenueData) => revenueData.revenueApr),
          Effect.tapError(() =>
            Effect.logWarning("Failed ❌: get revenue APR - falling back to 0")
          ),
          Effect.tapErrorCause((cause) => Console.log(Cause.pretty(cause))),
          Effect.catchAll(() => Effect.succeed(PERCENT_ZERO))
        ),
        pipe(
          MerklClient.getCampaignArray({
            activeOnly: true,
          }),
          Effect.tapError(() =>
            Effect.logWarning(
              "Failed ❌: get merkl campaign array - falling back to []"
            )
          ),
          Effect.tapErrorCause((cause) => Console.log(Cause.pretty(cause))),
          Effect.catchAll(() => Effect.succeed([] as MerklCampaign[]))
        ),
      ],
      { concurrency: "unbounded" }
    );

    const merklCampaignMap = createMerklCampaignMap(merklCampaigns);

    yield* Effect.log(
      `Begin 🎬: get markets for protocols: ${protocolKeys.join(", ")}`
    );

    const marketsByProtocol: MarketsByProtocol = yield* pipe(
      Effect.all(
        protocolKeys.map((protocolKey) =>
          getProtocolMarkets(protocolKey, strategy)
        ),
        { concurrency: "unbounded" }
      ),
      Effect.provideService(GlobalStateService, {
        revenueApr,
      }),
      Effect.provideService(MerklCampaignMapService, merklCampaignMap)
    );

    const marketsData: MarketsData = {
      marketsByProtocol: sortMarketsByDefaultOrder(marketsByProtocol),
      revenueApr,
    };

    return marketsData;
  });
}
